// YouTube Video Utilities
// Extract YouTube video ID from various URL formats

export const extractYouTubeVideoId = (url) => {
  if (!url) return null;
  
  // Handle different YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|m\.youtube\.com\/watch\?v=|youtube\.com\/watch\?.*&v=)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/ // Direct video ID
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  return null;
};

export const generateYouTubeThumbnail = (videoId, quality = 'hqdefault') => {
  if (!videoId) return null;
  
  // Available qualities: default (120x90), mqdefault (320x180), hqdefault (480x360), sddefault (640x480), maxresdefault (1280x720)
  // Note: hqdefault is the most reliable - maxresdefault may not be available for all videos
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
};

export const generateYouTubeEmbedUrl = (videoId) => {
  if (!videoId) return null;
  return `https://www.youtube.com/embed/${videoId}`;
};

export const generateYouTubeWatchUrl = (videoId) => {
  if (!videoId) return null;
  return `https://www.youtube.com/watch?v=${videoId}`;
};

export const validateYouTubeUrl = (url) => {
  const videoId = extractYouTubeVideoId(url);
  return videoId !== null;
};

export const createYouTubeVideoObject = (url, title = '') => {
  const videoId = extractYouTubeVideoId(url);
  
  if (!videoId) {
    throw new Error('URL YouTube invalide');
  }
  
  return {
    id: Date.now().toString(),
    title: title || `Vidéo YouTube ${videoId}`,
    type: 'video',
    youtube_id: videoId,
    youtube_url: generateYouTubeWatchUrl(videoId),
    thumbnail: generateYouTubeThumbnail(videoId),
    embed_url: generateYouTubeEmbedUrl(videoId),
    icon: 'fas fa-play-circle',
    duration: '', // Can be filled manually or via YouTube API
    platform: 'youtube'
  };
};

// Fetch video details from YouTube (requires API key - optional)
export const fetchYouTubeVideoDetails = async (videoId, apiKey = null) => {
  if (!apiKey) {
    // Return basic info without API
    return {
      title: `Vidéo YouTube ${videoId}`,
      duration: '',
      description: ''
    };
  }
  
  try {
    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?id=${videoId}&key=${apiKey}&part=snippet,contentDetails`
    );
    
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des détails YouTube');
    }
    
    const data = await response.json();
    
    if (!data.items || data.items.length === 0) {
      throw new Error('Vidéo YouTube non trouvée');
    }
    
    const video = data.items[0];
    
    // Parse duration (PT4M20S format)
    const duration = parseDuration(video.contentDetails.duration);
    
    return {
      title: video.snippet.title,
      description: video.snippet.description,
      duration: duration,
      publishedAt: video.snippet.publishedAt,
      channelTitle: video.snippet.channelTitle
    };
  } catch (error) {
    console.error('YouTube API Error:', error);
    return {
      title: `Vidéo YouTube ${videoId}`,
      duration: '',
      description: ''
    };
  }
};

// Parse YouTube duration format (PT4M20S) to human readable (4:20)
const parseDuration = (duration) => {
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  
  if (!match) return '';
  
  const hours = parseInt(match[1]) || 0;
  const minutes = parseInt(match[2]) || 0;
  const seconds = parseInt(match[3]) || 0;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  } else {
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
};
